import { useState } from "react";
import { Sparkles, Copy, Check, ExternalLink, Loader2, ChevronDown, RefreshCw } from "lucide-react";
import { BriefEditor } from "./BriefEditor";

const EMERGENT_URL = "https://app.emergent.sh";

const PRIORITY = {
  haute: "bg-gold text-navy",
  normale: "bg-sky-100 text-sky-800",
  basse: "bg-slate-100 text-slate-500",
};

const CopyButton = ({ text, i }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      data-testid={`admin-lead-copy-brief-${i}`}
      onClick={copy}
      className="flex items-center gap-1.5 rounded-full bg-navy px-3.5 py-1.5 text-[11px] font-semibold text-white transition-all hover:bg-navy-light"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Brief copié !" : "Copier le brief"}
    </button>
  );
};

export const LeadAiPanel = ({ lead, i, onAnalyze, onSaveBrief }) => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const ai = lead.ai;
  const pending = lead.ai_status === "queued" || lead.ai_status === "running";

  const analyze = async () => {
    if (running) return;
    setRunning(true);
    await onAnalyze(lead.id);
    setRunning(false);
  };

  if (!ai) {
    return (
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-dashed border-navy/15 bg-cream/60 px-4 py-3" data-testid={`admin-lead-ai-empty-${i}`}>
        <span className="flex items-center gap-2 text-xs text-slate-500">
          <Sparkles size={13} className="text-gold-dark" />
          {pending || running ? "Analyse IA en cours…" : lead.ai_status === "failed" ? "L'analyse IA a échoué." : "Pas encore analysée par l'agent IA."}
        </span>
        <button
          data-testid={`admin-lead-analyze-${i}`}
          onClick={analyze}
          disabled={running}
          className="flex items-center gap-1.5 rounded-full border border-navy/15 bg-white px-3 py-1.5 text-[11px] font-semibold text-navy transition-colors hover:border-gold hover:text-gold-dark disabled:opacity-60"
        >
          {running ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {running ? "Analyse…" : pending ? "Actualiser" : "Analyser"}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-4" data-testid={`admin-lead-ai-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-gold-dark">
          <Sparkles size={12} /> Agent IA
        </span>
        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${PRIORITY[ai.priority] || PRIORITY.normale}`} data-testid={`admin-lead-ai-priority-${i}`}>
          Priorité {ai.priority}
        </span>
        <span className="rounded-full bg-navy px-2.5 py-0.5 text-[10px] font-semibold text-gold" data-testid={`admin-lead-ai-offer-${i}`}>
          Conseil : {ai.recommended_offer}
        </span>
        {ai.sector && <span className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-medium text-slate-600">{ai.sector}</span>}
      </div>
      <p className="mt-3 text-sm leading-relaxed text-slate-700" data-testid={`admin-lead-ai-summary-${i}`}>{ai.summary}</p>
      {ai.missing_info?.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-slate-600">
          {ai.missing_info.map((m) => (
            <li key={m} className="flex gap-2"><span className="text-gold-dark">•</span> {m}</li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <CopyButton text={ai.brief} i={i} />
        <a
          data-testid={`admin-lead-open-emergent-${i}`}
          href={EMERGENT_URL}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1.5 rounded-full border border-navy/15 bg-white px-3.5 py-1.5 text-[11px] font-semibold text-navy transition-colors hover:border-gold hover:text-gold-dark"
        >
          <ExternalLink size={12} /> Ouvrir Emergent
        </a>
        <button
          data-testid={`admin-lead-toggle-brief-${i}`}
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 transition-colors hover:text-navy"
        >
          {open ? "Masquer le brief" : "Voir le brief"} <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <button
          data-testid={`admin-lead-reanalyze-${i}`}
          onClick={analyze}
          disabled={running}
          aria-label="Régénérer l'analyse"
          title="Régénérer"
          className="ml-auto flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-white hover:text-navy disabled:opacity-60"
        >
          {running ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        </button>
      </div>
      {open && <BriefEditor lead={lead} i={i} onSave={onSaveBrief} />}
    </div>
  );
};
